import { useMemo } from 'react';
import { useObservable } from '@paradigm/utils/src/hooks';
import { useUnifiedRfqsService } from '#/unified-rfqs/contexts/unified-rfqs';
import { useUnifiedTradesService } from '#/unified-rfqs/contexts/unified-trades';
import {
  isTradeFilled,
  getTradeTime,
  Trade,
} from '#/unified-rfqs/entities/shared';
import { isRecentTrade } from './shared';
import { TradeMatchData } from './types';
import useFetchTradeHistoryOnStart from './useFetchTradeHistoryOnStart';

/**
 * Listen for for recent user trades that have open OBs with the same structure.
 * (matches the description)
 */
export default function useRecentTradesListener() {
  useFetchTradeHistoryOnStart();
  const tradesService = useUnifiedTradesService();
  const unifiedRfqsService = useUnifiedRfqsService();
  const tradesListIds = useObservable(tradesService.listIds());
  const rfqsIds = useObservable(unifiedRfqsService.listIds());

  const recentTradesWithObs = useMemo(() => {
    const tradeData: TradeMatchData[] = [];
    const obDescriptions = new Map<string, number>();
    rfqsIds.forEach((rfqId) => {
      const rfq = unifiedRfqsService.selectById(rfqId);
      if (rfq?.type !== 'grfq') return;
      obDescriptions.set(rfq.description, rfq.id);
    });

    tradesListIds.forEach((tradeId) => {
      const trade = tradesService.selectById(tradeId);
      if (
        trade == null ||
        !isTradeFilled(trade) ||
        !isRecentTrade(getTradeTime(trade))
      )
        return;
      const obId = obDescriptions.get(trade.description);
      if (obId == null) return;
      tradeData.push(toTradeMatchData(trade, obId));
    });
    return tradeData;
  }, [rfqsIds, tradesListIds, tradesService, unifiedRfqsService]);

  return recentTradesWithObs;
}

function toTradeMatchData(trade: Trade, obId: number) {
  return {
    rfqType: trade.rfqType,
    tradedAt: getTradeTime(trade),
    quantity: trade.quantity,
    tradeId: trade.id,
    rfqId: trade.rfq_id,
    side: trade.rfqType === 'grfq' ? trade.action! : trade.side,
    obId,
    description: trade.description,
    price: trade.price,
    markPrice: trade.mark_price,
  };
}
